import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Cog6ToothIcon, HomeIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import {
  Cog6ToothIcon as CogSolid,
  HomeIcon as HomeSolid,
  BookOpenIcon as BookSolid,
} from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import { brand } from '../config/brand';

interface MobileLayoutProps {
  children: React.ReactNode;
  showNav?: boolean;
  showHeader?: boolean;
  headerTitle?: string;
  headerAction?: React.ReactNode;
  showBackButton?: boolean;
  transparentContent?: boolean;
}

export default function MobileLayout({
  children,
  showNav = true,
  showHeader = true,
  headerTitle,
  headerAction,
  showBackButton = false,
  transparentContent = false,
}: MobileLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const navItemsById = {
    home: {
      path: '/dashboard',
      icon: HomeIcon,
      activeIcon: HomeSolid,
      label: t('navigation.home', 'Home'),
    },
    stories: {
      path: '/stories',
      icon: BookOpenIcon,
      activeIcon: BookSolid,
      label: t('navigation.stories', 'Stories'),
    },
    settings: {
      path: '/profile',
      icon: Cog6ToothIcon,
      activeIcon: CogSolid,
      label: t('navigation.settings', 'Settings'),
    },
  } as const;

  // Bottom-nav order is brand-driven (e.g. Stories-first for Plotweave).
  const navItems = brand.navOrder.map((id) => navItemsById[id]);

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <div
      className="h-full flex flex-col"
      style={{ background: 'var(--ds-bg-primary, var(--color-light-bg))' }}
    >
      {/* Header */}
      {showHeader && (
        <header
          className="border-b"
          style={{
            background: 'var(--ds-bg-card, var(--color-light-card))',
            borderColor: 'var(--ds-border, var(--color-light-border))',
          }}
        >
          <div className="mobile-container">
            <div className="flex items-center justify-between h-14">
              {showBackButton ? (
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 -ml-2 text-light-text-secondary dark:text-dark-text-secondary"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
              ) : (
                <div className="w-10" />
              )}

              {headerTitle ? (
                <h1 className="text-lg font-semibold text-light-text dark:text-dark-text">
                  {headerTitle}
                </h1>
              ) : brand.logo ? (
                <img src={brand.logo} alt={brand.name} className="h-6 w-auto" />
              ) : (
                <span className="text-lg font-semibold text-light-text dark:text-dark-text">
                  {brand.name}
                </span>
              )}

              <div className="w-10">{headerAction}</div>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main
        className="flex-1 overflow-y-auto"
        {...(transparentContent ? { 'data-dreamscape-transparent': true } : {})}
      >
        <div className="mobile-container">{children}</div>
      </main>

      {/* Bottom Navigation */}
      {showNav && (
        <nav
          className="border-t"
          style={{
            background: 'var(--ds-bg-card, var(--color-light-card))',
            borderColor: 'var(--ds-border, var(--color-light-border))',
          }}
        >
          <div className="mobile-container">
            <div className="flex items-center justify-around h-16">
              {navItems.map((item) => {
                const Icon = isActive(item.path) ? item.activeIcon : item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="flex flex-col items-center justify-center flex-1 py-2"
                  >
                    <Icon
                      className={`w-6 h-6 ${
                        isActive(item.path)
                          ? 'text-primary-500'
                          : 'text-light-text-secondary dark:text-dark-text-secondary'
                      }`}
                    />
                    <span
                      className={`text-xs mt-1 ${
                        isActive(item.path)
                          ? 'text-primary-500'
                          : 'text-light-text-secondary dark:text-dark-text-secondary'
                      }`}
                    >
                      {item.label}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>
      )}
    </div>
  );
}
